import React, {
  useEffect, useCallback, useState, useRef,
} from 'react';
import { func } from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import Form, { FormConsumer } from '@active/react-ui/Form';
import FormGroup from '@active/react-ui/FormGroup';
import FormField from '@active/react-ui/FormField';
import FormLabel from '@active/react-ui/FormLabel';
import Grid from '@active/react-ui/Grid';
import { useSelector } from 'react-redux';
import GridItem from '@active/react-ui/GridItem';
import Locale from '@active/react-utils/i18n/Locale';
import { withI18n } from '@active/react-utils/i18n/LocaleContext';
import Input from '@active/react-ui/Input';
import pubSubInstance from '../../../../../../../shared/utils/pubSub';
import CheckScanner, { CheckImageButton, CheckFieldWarning } from '../../../../../CheckScanner';
import useCurrentAmountAndCreditBalance from '../../../../../../../shared/utils/currentAmountAndCreditBalance/useCurrentAmountAndCreditBalance';

const Check = ({ i18n }) => {
  const paymentForm = useRef();
  const [ showWarning, setShowWarning ] = useState(false);
  const [ checkScannerInfo, setCheckScannerInfo ] = useState(null);
  const {
    widgetNameSpace,
    checkScanner: {
      enabled,
      endorsementContent,
    } = {},
  } = useSelector(state => state.propsData);
  const { yourPayment } = useCurrentAmountAndCreditBalance();

  const checkEqual = useCallback((_checkNumber, checkScanner) => {
    if (checkScanner) {
      setShowWarning(_checkNumber !== checkScanner.micrCheckNumber);
    }
  }, []);

  const saveCallback = useCallback((values) => {
    const { _checkNumber } = values;
    paymentForm.current.setFieldValue('checkNumber', _checkNumber);
    setCheckScannerInfo(values);
    checkEqual(_checkNumber, values);
  }, [ checkEqual, paymentForm.current ]);

  const handleFormChange = useCallback(({ values }) => {
    const { checkNumber } = values;
    checkEqual(checkNumber, checkScannerInfo);
  }, [ checkEqual, checkScannerInfo ]);

  const checkNumberValidator = (value) => {
    const reg = /^\w{1,100}$/;
    if (!reg.test(value)) {
      return i18n('checkout.lib.components.payment.check.valid.checkNumber');
    }
    return '';
  };

  const handleValidate = useCallback(() => {
    return new Promise((resolve) => {
      paymentForm.current.validateForm({ shouldTouchFields: true }).then((res) => {
        const formValid = isEmpty(res);
        resolve(formValid);
      });
    });
  }, [ paymentForm.current ]);

  const handleSubmit = useCallback(() => {
    const { values: { checkNumber } } = paymentForm.current;
    const submitScannerInfo = checkScannerInfo ? omit(checkScannerInfo, [ '_checkNumber', 'scanStatus', 'micrCheckNumber' ]) : {};
    return Promise.resolve({
      status: true,
      checkNumber,
      paymentMethodType: 'CHECK',
      ...submitScannerInfo,
    });
  }, [ paymentForm.current, checkScannerInfo ]);

  useEffect(() => {
    pubSubInstance.subscribe(`${widgetNameSpace}-payment-item-validate`, handleValidate);
    pubSubInstance.subscribe(`${widgetNameSpace}-payment-item-submit`, handleSubmit);
    return () => {
      pubSubInstance.unsubscribe(`${widgetNameSpace}-payment-item-validate`, handleValidate);
      pubSubInstance.unsubscribe(`${widgetNameSpace}-payment-item-submit`, handleSubmit);
    };
  }, [ widgetNameSpace, handleValidate, handleSubmit ]);

  return (<div className="check cui-mg-t-20">
    {enabled && <CheckScanner
      checkPaidAmount={yourPayment}
      saveCallback={saveCallback}
      endorsementContent={endorsementContent} />
    }
    <Form
      onChange={handleFormChange}
      customizeLayout>
      <Grid container={false} rowGap={0} colGap={0} >
        <GridItem span={12} spanXl={8}>
          <FormGroup name="checkNumber" required>
            <FormLabel >
              <Locale id="checkout.lib.components.payment.check.checkNumber" />
            </FormLabel>
            <FormField
              maxLength="100"
              autoComplete="off"
              id="checkNumber"
              validate={checkNumberValidator}
              as={Input}/>
            {showWarning && <CheckFieldWarning />}
          </FormGroup>
        </GridItem>
        {checkScannerInfo && <CheckImageButton />}
      </Grid>
      <FormConsumer>
        {
          ({
            form,
          }) => {
            paymentForm.current = form;
          }
        }
      </FormConsumer>
    </Form>
  </div>);
};

Check.propTypes = {
  i18n: func.isRequired,
};

export default withI18n(Check);
